import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, Calendar, Users, Heart, Filter, Play, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoWithFallback = ({ src, name, slug }) => {
  const colorMap = {
    'afcon': 'from-amber-500 to-yellow-600',
    'caf-champions': 'from-emerald-500 to-teal-600',
    'chan': 'from-blue-500 to-indigo-600',
    'wafcon': 'from-pink-500 to-rose-600',
    'npfl': 'from-green-600 to-emerald-700',
    'psl': 'from-purple-500 to-violet-600',
  };
  const gradient = colorMap[slug] || 'from-gray-500 to-gray-600';
  const abbrev = name.replace(/[a-z\s]/g, '').slice(0, 3) || name.charAt(0);
  return (
    <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
      <span className="text-white font-black text-sm tracking-tight">{abbrev}</span>
    </div>
  );
};

const FILTERS = [
  { id: 'popular', label: 'Popular' },
  { id: 'live', label: 'Live Now' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'domestic', label: 'Domestic' },
  { id: 'continental', label: 'Continental' },
];

const STATUS_CONFIG = {
  active:   { text: 'LIVE', cls: 'bg-red-600 text-white animate-pulse' },
  live:     { text: 'LIVE', cls: 'bg-red-600 text-white animate-pulse' },
  upcoming: { text: 'Upcoming', cls: 'bg-amber-500/20 text-amber-400 border border-amber-500/30' },
  completed: { text: 'Completed', cls: 'bg-white/5 text-white/50 border border-white/10' },
};

export default function Competitions() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('popular');
  const [followedComps, setFollowedComps] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch competitions from backend
  const loadCompetitions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/competitions`);
      setCompetitions(res.data.competitions || []);
    } catch (error) {
      console.error('Failed to load competitions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitions();
    loadFollowed();
  }, [loadCompetitions]);

  const loadFollowed = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/user/following`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setFollowedComps((res.data.competitions || []).map(c => c.toLowerCase()));
    } catch { /* not logged in */ }
  };

  const toggleFollow = async (slug) => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }

    const isFollowing = followedComps.includes(slug.toLowerCase());
    try {
      if (isFollowing) {
        await axios.post(`${API}/user/unfollow-competition`, { competition_slug: slug }, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
        });
        setFollowedComps(prev => prev.filter(c => c !== slug.toLowerCase()));
      } else {
        await axios.post(`${API}/user/follow-competition`, { competition_slug: slug }, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
        });
        setFollowedComps(prev => [...prev, slug.toLowerCase()]);
      }
    } catch { /* silent */ }
  };

  const filtered = (() => {
    let list = [...competitions];
    switch (activeFilter) {
      case 'live':        list = list.filter(c => c.status === 'active' || c.live_matches > 0); break;
      case 'upcoming':    list = list.filter(c => c.status === 'upcoming'); break;
      case 'domestic':    list = list.filter(c => c.category === 'domestic'); break;
      case 'continental': list = list.filter(c => c.category === 'continental'); break;
      default: break; // popular — show all
    }
    // Sort: live → following → teams_count
    list.sort((a, b) => {
      const aLive = (a.status === 'active' || a.live_matches > 0) ? 0 : 1;
      const bLive = (b.status === 'active' || b.live_matches > 0) ? 0 : 1;
      if (aLive !== bLive) return aLive - bLive;
      const aF = followedComps.includes(a.slug?.toLowerCase()) ? 0 : 1;
      const bF = followedComps.includes(b.slug?.toLowerCase()) ? 0 : 1;
      if (aF !== bF) return aF - bF;
      return (b.teams_count || 0) - (a.teams_count || 0);
    });
    return list;
  })();

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* Hero Header */}
      <div className="relative pt-20 pb-14 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1740906792290-669025a709e5?w=1600&q=60)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/55 via-[#0a0a0a]/50 to-[#0a0a0a]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/50 hover:text-white mb-6 transition-colors text-sm"
            data-testid="competitions-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Home
          </button>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" data-testid="competitions-title">
              Competitions on AfroSports
            </h1>
            <p className="text-white/60 text-base sm:text-lg max-w-xl">
              Follow the leagues that define African football worldwide.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {/* Sorting Filter */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto scrollbar-hide -mx-4 px-4 py-1" data-testid="competition-filters">
          <Filter className="w-4 h-4 text-white/30 shrink-0" />
          {FILTERS.map(f => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeFilter === f.id
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
              }`}
            >
              {f.id === 'live' && <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full mr-1.5 animate-pulse" />}
              {f.label}
            </button>
          ))}
        </div>

        {/* Competitions Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-white/30">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No competitions match this filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((comp, i) => {
              const isFollowing = followedComps.includes(comp.slug?.toLowerCase());
              const cfg = STATUS_CONFIG[comp.status] || STATUS_CONFIG.upcoming;

              return (
                <motion.div
                  key={comp.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  whileHover={{ scale: 1.03 }}
                  className="group relative bg-[#141414] border border-white/5 rounded-xl overflow-hidden hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-900/20 transition-all duration-200"
                  data-testid={`competition-card-${comp.slug}`}
                >
                  {/* LIVE badge — top left red pill */}
                  {(comp.status === 'active' || comp.live_matches > 0) && (
                    <div className="absolute top-3 left-3 z-10">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-600 text-white text-[11px] font-bold rounded-full shadow-lg shadow-red-600/30" data-testid={`live-badge-${comp.slug}`}>
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                        LIVE {comp.live_matches > 0 && `(${comp.live_matches})`}
                      </span>
                    </div>
                  )}

                  <Link to={`/competitions/${comp.slug}`} className="block p-5">
                    <div className="flex items-start gap-4">
                      {/* Logo */}
                      <div className="w-16 h-16 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden group-hover:border-emerald-500/30 transition-colors">
                        <LogoWithFallback name={comp.short_name || comp.name} slug={comp.slug} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-base truncate group-hover:text-emerald-400 transition-colors mb-1">
                          {comp.short_name || comp.name}
                        </h3>
                        <p className="text-white/40 text-sm leading-snug line-clamp-2 mb-3">
                          {comp.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-white/30">
                          {comp.teams_count && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{comp.teams_count} teams</span>}
                          {comp.country && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{comp.country}</span>}
                        </div>
                      </div>
                    </div>

                    {/* Status badge — only for non-active */}
                    {comp.status !== 'active' && comp.live_matches === 0 && (
                      <div className="mt-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full ${cfg.cls}`} data-testid={`status-${comp.slug}`}>
                          {cfg.text}
                        </span>
                      </div>
                    )}
                  </Link>

                  {/* Follow button — outside <Link> */}
                  <div className="absolute top-4 right-4 z-10">
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFollow(comp.slug); }}
                      data-testid={`follow-${comp.slug}`}
                      className={`p-2 rounded-full transition-all ${
                        isFollowing
                          ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-white/5 text-white/30 hover:text-white hover:bg-white/10 border border-transparent'
                      }`}
                      title={isFollowing ? 'Following' : 'Follow competition'}
                    >
                      <Heart className={`w-4 h-4 ${isFollowing ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="bg-[#141414] border border-white/5 rounded-2xl p-8 sm:p-12 max-w-3xl mx-auto">
            <Trophy className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-3">
              Don't Miss a Match
            </h2>
            <p className="text-white/40 mb-6 max-w-md mx-auto">
              Subscribe now for unlimited access to all competitions, live matches, and exclusive content.
            </p>
            <Button
              onClick={() => navigate('/subscription')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-5 text-base rounded-full"
              data-testid="subscribe-cta"
            >
              View Plans — Starting at $9.99/mo
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
